/**
 * @author      Antigravity
 * @date        May, 2026
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState } from 'react';
import { Button, Col, Container, Row, Table, Card, Modal, Form } from 'react-bootstrap';
import { DatatableWrapper, Filter, Pagination, PaginationOptions, TableBody, TableHeader } from 'react-bs-datatable';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DataApi from '../../../api/DataApi';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import Confirm from '../../Confirm';

const WatconnectModuleConfigList = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  
  // Object & Field dynamic states
  const [objectList, setObjectList] = useState([]);
  const [fieldList, setFieldList] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  
  // Form input states
  const [formValues, setFormValues] = useState({
    id: '',
    object_name: '',
    field_name: '',
    is_active: true
  });

  const dataApi = new DataApi('watconnect/watconnect_module_config');

  useEffect(() => {
    fetchConfigs();
    loadObjects();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const res = await dataApi.fetchAll();
      if (res.data?.success && res.data?.data) {
        setConfigs(res.data.data);
      } else {
        setConfigs([]);
      }
    } catch (err) {
      console.error('Error fetching watconnect module configs:', err);
      toast.error('Failed to load configurations.');
    } finally {
      setLoading(false);
    }
  };

  const loadObjects = async () => {
    try {
      const res = await ObjectConfigApi.fetchObjectConfigs();
      if (res.success && res.data) {
        const allowedObjects = res.data.filter(obj => !obj.is_deleted);
        setObjectList(allowedObjects);
      }
    } catch (err) {
      console.error('Failed to load system objects:', err);
    }
  };

  const loadFieldsForObject = async (objectName) => {
    if (!objectName) {
      setFieldList([]);
      return;
    }
    try {
      setLoadingFields(true);
      const res = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
      if (res.success && res.data?.fields) {
        // Filter fields to only show those that have data_type of 'phone'
        const phoneFields = res.data.fields.filter(f => f.data_type === 'phone');
        setFieldList(phoneFields);
      } else {
        setFieldList([]);
      }
    } catch (err) {
      console.error('Failed to load fields for object:', objectName, err);
      toast.error('Failed to load object fields.');
    } finally {
      setLoadingFields(false);
    }
  };

  const handleOpenAddModal = () => {
    setFormValues({
      id: '',
      object_name: '',
      field_name: '',
      is_active: true
    });
    setFieldList([]);
    setSelectedRecord(null);
    setShowModal(true);
  };

  const handleOpenEditModal = async (row) => {
    setSelectedRecord(row);
    setFormValues({
      id: row.id,
      object_name: row.object_name || '',
      field_name: row.field_name || '',
      is_active: row.is_active !== false
    });
    setShowModal(true);
    await loadFieldsForObject(row.object_name);
  };

  const handleOpenDeleteModal = (row) => {
    setSelectedRecord(row);
    setShowDeleteModal(true);
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'object_name') {
      setFormValues(prev => ({
        ...prev,
        field_name: '' // Reset field selection when object changes
      }));
      await loadFieldsForObject(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValues.object_name || !formValues.field_name) {
      toast.error('Both Object and WhatsApp Field are required.');
      return;
    }

    const isDuplicate = configs.some(
      c =>
        c.object_name?.toLowerCase() === formValues.object_name?.toLowerCase() &&
        c.field_name?.toLowerCase() === formValues.field_name?.toLowerCase() &&
        c.id !== formValues.id
    );

    if (isDuplicate) {
      toast.error(
        `A configuration for object '${formValues.object_name}' with field '${formValues.field_name}' already exists.`
      );
      return;
    }

    try {
      // Create/Update uses POST /api/watconnect/:table with upsert in backend
      const payload = {
        object_name: formValues.object_name,
        field_name: formValues.field_name,
        is_active: formValues.is_active
      };

      if (formValues.id) {
        payload.id = formValues.id;
      }

      const res = await dataApi.post('', payload);
      if (res.data?.success) {
        toast.success(formValues.id ? 'Configuration updated successfully.' : 'Configuration created successfully.');
        setShowModal(false);
        fetchConfigs();
      } else {
        toast.error(res.data?.message || 'Failed to save configuration.');
      }
    } catch (err) {
      console.error('Error saving configuration:', err);
      toast.error(err.response?.data?.message || 'Error saving configuration.');
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;
    try {
      const res = await dataApi.delete(selectedRecord.id);
      if (res.data?.success) {
        toast.success('Configuration deleted successfully.');
        setShowDeleteModal(false);
        fetchConfigs();
      } else {
        toast.error(res.data?.message || 'Failed to delete configuration.');
      }
    } catch (err) {
      console.error('Error deleting configuration:', err);
      toast.error('Error deleting configuration.');
    }
  };

  const handleToggleActive = async (row, isChecked) => {
    try {
      const payload = {
        id: row.id,
        object_name: row.object_name,
        field_name: row.field_name,
        is_active: isChecked
      };
      const res = await dataApi.post('', payload);
      if (res.data?.success) {
        toast.success(`Configuration ${isChecked ? 'activated' : 'deactivated'} successfully.`);
        fetchConfigs();
      } else {
        toast.error(res.data?.message || 'Failed to update status.');
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      toast.error('Error toggling active status.');
    }
  };

  const labels = { beforeSelect: " " };
  const header = [
    { 
      title: "Object Name", 
      prop: "object_name", 
      isFilterable: true,
      cell: (row) => {
        const obj = objectList.find(o => o.object_name === row.object_name);
        return (
          <span className="text-dark fw-medium text-capitalize" style={{ fontSize: '0.9rem' }}>
            {obj?.object_label || row.object_name}
          </span>
        );
      }
    },
    { 
      title: "WhatsApp/Phone Field", 
      prop: "field_name", 
      isFilterable: true,
      cell: (row) => (
        <code className="text-primary fw-semibold" style={{ fontSize: '0.9rem' }}>
          {row.field_name}
        </code>
      )
    },
    {
      title: "Active",
      prop: "is_active",
      cell: (row) => {
        return (
          <Form.Check 
            type="switch"
            id={`active-switch-${row.id}`}
            checked={row.is_active !== false}
            onChange={(e) => handleToggleActive(row, e.target.checked)}
          />
        );
      }
    },
    {
      title: "Actions",
      prop: "id",
      cell: (row) => (
        <>
          <Button
            className='btn-sm mx-2'
            variant='primary'
            onClick={() => handleOpenEditModal(row)}
            title='Edit'
            style={{ borderRadius: '6px' }}
          >
            <i className='fa-regular fa-pen-to-square'></i> Edit
          </Button>
          <Button
            className='btn-sm mx-2'
            variant='danger'
            onClick={() => handleOpenDeleteModal(row)}
            title='Delete'
            style={{ borderRadius: '6px' }}
          >
            <i className='fa-regular fa-trash-can'></i> Delete
          </Button>
        </>
      ),
    },
  ];

  return (
    <>
      <Container className="mt-5">
        <Row className="mx-5 mb-4">
          <Card className="p-4 bg-white shadow-sm border-0 rounded-3">
            <h5 className="mb-2 fw-bold">💡 Quick Guide</h5>
            <small>
              Use this dashboard to configure how campaigns retrieve contact information dynamically. You can register any standard or custom objects (e.g. Lead, Contact, Custom Object) and specify which of their fields holds the recipient's phone/WhatsApp number. This removes hardcoded dependencies and allows campaigns to target any record type.
            </small>
          </Card>
        </Row>

        <Row className="mx-5 g-0">
          <Col lg={12} className="mb-3">
            <Row className="g-0">
              <Col lg={12}>
                <div className="bg-white shadow-sm border-0 rounded-3 p-4">
                  <h5 className="fw-semibold mb-1">📦 Recipient Module Configurations</h5>

                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 text-muted">Retrieving configurations...</p>
                    </div>
                  ) : (
                    <DatatableWrapper
                      body={configs}
                      headers={header}
                      paginationOptionsProps={{
                        initialState: {
                          rowsPerPage: 10,
                          options: [5, 10, 15, 20],
                        },
                      }}
                    >
                      <Row className="mb-3">
                        <Col
                          lg={3}
                          sm={10}
                          xs={10}
                          className="d-flex flex-col justify-content-end align-items-end"
                        >
                          <Filter />
                        </Col>
                        <Col
                          lg={4}
                          sm={2}
                          xs={2}
                          className="d-flex flex-col justify-content-start align-items-start"
                        >
                          <PaginationOptions labels={labels} />
                        </Col>
                        <Col
                          lg={5}
                          sm={12}
                          xs={12}
                          className="mt-2 d-flex flex-col justify-content-end align-items-end"
                        >
                          <Button
                            variant="primary"
                            className="new-list-btn"
                            onClick={handleOpenAddModal}
                          >
                            <i className="fa-solid fa-plus"></i> Add Dynamic Config
                          </Button>
                        </Col>
                      </Row>

                      <div className="table-responsive">
                        <Table striped bordered className="related-list-table" responsive="sm">
                          <TableHeader />
                          <TableBody />
                        </Table>
                      </div>
                      <Pagination />
                    </DatatableWrapper>
                  )}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered backdrop="static" animation={false}>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="fw-bold" style={{ fontSize: '1.35rem' }}>
            {formValues.id ? 'Edit Configuration' : 'Add New Configuration'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="objectName">
                  <Form.Label className="fw-medium text-dark">Target Object</Form.Label>
                  <Form.Select
                    name="object_name"
                    value={formValues.object_name}
                    onChange={handleInputChange}
                    required
                    style={{ height: '42px', borderRadius: '6px' }}
                  >
                    <option value="">-- Select Object --</option>
                    {objectList.map(obj => (
                      <option key={obj.id} value={obj.object_name}>
                        {obj.object_label || obj.object_name} ({obj.object_name})
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">The system object to fetch recipient records from.</Form.Text>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group controlId="fieldName">
                  <Form.Label className="fw-medium text-dark">WhatsApp / Phone Field</Form.Label>
                  <Form.Select
                    name="field_name"
                    value={formValues.field_name}
                    onChange={handleInputChange}
                    required
                    disabled={!formValues.object_name || loadingFields}
                    style={{ height: '42px', borderRadius: '6px' }}
                  >
                    <option value="">
                      {loadingFields ? '-- Loading fields... --' : '-- Select Field --'}
                    </option>
                    {fieldList.map(f => (
                      <option key={f.id} value={f.field_name}>
                        {f.field_label || f.label || f.field_name} ({f.field_name})
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">The field that stores the phone number / WhatsApp number.</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group controlId="isActive">
                  <Form.Check 
                    type="switch"
                    label="Is Active"
                    name="is_active"
                    checked={formValues.is_active}
                    onChange={(e) => setFormValues(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="fw-medium text-dark mt-2"
                  />
                  <Form.Text className="text-muted">
                    Enable or disable this recipient field configuration for campaigns and inbound message matching.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-top-0 pt-0">
          <Button variant="light" className="fw-medium px-4" onClick={() => setShowModal(false)} style={{ borderRadius: '6px' }}>
            Close
          </Button>
          <Button variant="primary" className="fw-medium px-4 text-white" onClick={handleSubmit}
            disabled={!formValues.object_name || !formValues.field_name}
            style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)', borderRadius: '6px' }}>
            Save Configuration
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <Confirm
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          deleteModuleRecord={handleDelete}
          title="Confirm Delete?"
          message={`Are you sure you want to delete this configuration? Campaign recipients configured under this will default to standard Leads.`}
          table="module"
        />
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default WatconnectModuleConfigList;
